import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type PermissionFilterJSON = z.infer<typeof PermissionFilterSchema.PermissionFilterJSONZ>;

export type PermissionFilterParametersProps = z.infer<typeof PermissionFilterSchema.PermissionFilterZ>;

export default class PermissionFilterSchema {
  public static readonly PermissionFilterJSONZ = z.object({
    hasParent: z.boolean().nullish(),
    resource: z.unknown().nullish(),
    parentResource: z.unknown().nullish(),
    parentId: z.uuid().nullish(),
    legalEntityId: z.uuid().nullish(),
    granteeUserIds: z.array(z.uuid()).nullish(),
    grantorUserId: z.uuid().nullish(),
    minCreatedDate: z.iso.datetime({ offset: true }).nullish(),
    maxCreatedDate: z.iso.datetime({ offset: true }).nullish(),
    showDisabled: z.boolean().nullish(),
    minDisabledDate: z.iso.datetime({ offset: true }).nullish(),
    maxDisabledDate: z.iso.datetime({ offset: true }).nullish(),
    categories: z.array(z.string()).nullish(),
    accessType: z.string().nullish(),
    name: z.string().nullish(),
  });
  public static readonly PermissionFilterZ = z.object({
    hasParent: z.boolean().optional(),
    resource: z.unknown().optional(),
    parentResource: z.unknown().optional(),
    parentId: z.instanceof(Guid).optional(),
    legalEntityId: z.instanceof(Guid).optional(),
    granteeUserIds: z.array(z.instanceof(Guid)).optional(),
    grantorUserId: z.instanceof(Guid).optional(),
    minCreatedDate: DateSchema.type.optional(),
    maxCreatedDate: DateSchema.type.optional(),
    showDisabled: z.boolean().optional(),
    minDisabledDate: DateSchema.type.optional(),
    maxDisabledDate: DateSchema.type.optional(),
    categories: z.array(z.string()).optional(),
    accessType: z.string().optional(),
    name: z.string().optional(),
  });

  public static validateJSON(values: unknown): PermissionFilterParametersProps {
    return parseOrThrow(PermissionFilterSchema.PermissionFilterZ, values, {
      logError: true,
      schemaName: "PermissionFilter",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is PermissionFilterParametersProps {
    try {
      PermissionFilterSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: PermissionFilterJSON): PermissionFilterParametersProps {
    const parsed = parseOrThrow(PermissionFilterSchema.PermissionFilterJSONZ, data, {
      logError: true,
      schemaName: "PermissionFilter",
      operation: "deserialize",
      payloadSample: data,
    });
    return PermissionFilterSchema.codec.decode(parsed);
  }

  public static serialize(filter: PermissionFilterParametersProps): PermissionFilterJSON {
    const parsed = PermissionFilterSchema.validateJSON(filter);
    return PermissionFilterSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    PermissionFilterSchema.PermissionFilterJSONZ,
    PermissionFilterSchema.PermissionFilterZ,
    {
      decode: (data) => ({
        hasParent: data.hasParent ?? undefined,
        resource: data.resource,
        parentResource: data.parentResource,
        parentId: Guid.fromJSON(data.parentId) ?? undefined,
        legalEntityId: Guid.fromJSON(data.legalEntityId) ?? undefined,
        granteeUserIds: Guid.fromJSONArray(data.granteeUserIds) ?? undefined,
        grantorUserId: Guid.fromJSON(data.grantorUserId) ?? undefined,
        minCreatedDate: AHDate.fromJSON(data.minCreatedDate) ?? undefined,
        maxCreatedDate: AHDate.fromJSON(data.maxCreatedDate) ?? undefined,
        showDisabled: data.showDisabled ?? undefined,
        minDisabledDate: AHDate.fromJSON(data.minDisabledDate) ?? undefined,
        maxDisabledDate: AHDate.fromJSON(data.maxDisabledDate) ?? undefined,
        categories: data.categories ?? undefined,
        accessType: data.accessType ?? undefined,
        name: data.name ?? undefined,
      }),
      encode: (filter) => ({
        hasParent: filter.hasParent,
        resource: filter.resource,
        parentResource: filter.parentResource,
        parentId: filter.parentId?.toJSON(),
        legalEntityId: filter.legalEntityId?.toJSON(),
        granteeUserIds: filter.granteeUserIds?.map((id) => id.toJSON()),
        grantorUserId: filter.grantorUserId?.toJSON(),
        minCreatedDate: filter.minCreatedDate?.toISOString(),
        maxCreatedDate: filter.maxCreatedDate?.toISOString(),
        showDisabled: filter.showDisabled,
        minDisabledDate: filter.minDisabledDate?.toISOString(),
        maxDisabledDate: filter.maxDisabledDate?.toISOString(),
        categories: filter.categories,
        accessType: filter.accessType,
        name: filter.name,
      }),
    }
  );
}
